import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, X } from "lucide-react";
import { LGoBadge, LGoButton } from "@/ui/branded";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/primitives/collapsible";
import { CustomFieldAnswers } from "@/features/custom-fields/components/CustomFieldAnswers";
import { canBeCancelled, cancellationEnding } from "@/domain/reservation/rules";
import type { CancellationEnding } from "@/domain/reservation/rules";
import type { PartnerReservation } from "@/domain/reservation/types";
import { formatDateTime } from "@/lib/time";
import { formatPrice } from "@/lib/money";
import { CancelReservationDialog } from "./CancelReservationDialog";
import { LapsedConfirmationTimeline } from "./ConfirmationDeadline";

interface ReservationDisclosure {
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
}

interface ReservationRowProps {
  reservation: PartnerReservation;
  isCancelling: boolean;
  onCancel: (reservationId: number, options: { onSettled: () => void }) => void;
  /** Given to the closed rows only: an archive is scanned and then opened at
   *  one row, while the rows still ahead are a working queue and stay whole. */
  disclosure?: ReservationDisclosure;
}

type StateBadge = { label: string; tone: "gold" | "success" | "neutral" | "danger" };

const stateBadge: Record<string, StateBadge> = {
  pending_confirmation: { label: "Čeká na potvrzení", tone: "gold" },
  pending_payment: { label: "Čeká na platbu", tone: "neutral" },
  upcoming: { label: "Potvrzeno", tone: "success" },
  finished: { label: "Proběhlo", tone: "neutral" },
};

/** A partner who lets the answering window lapse loses the booking without ever
 *  seeing it happen, so that ending names them and the other two name their
 *  actor — the three must not read as one „Zrušeno". */
const cancellationBadge: Record<CancellationEnding, StateBadge> = {
  expired: { label: "Nepotvrdili jste včas", tone: "danger" },
  business: { label: "Zrušeno podnikem", tone: "neutral" },
  customer: { label: "Zrušeno zákazníkem", tone: "neutral" },
  unknown: { label: "Zrušeno", tone: "neutral" },
};

/** Says which of the several endings the badge folds into one — „Zrušeno
 *  podnikem" covers a rejection, a change of mind and a payment the account
 *  could not take. A reason nobody recorded and one this build does not know
 *  both say nothing: a guessed explanation is the one the partner then repeats
 *  to the customer. */
const outcomeByCancellationReason: Record<string, string> = {
  confirmation_expired: "Rezervace se zrušila sama, protože jste ji nepotvrdili do konce lhůty.",
  business_reject: "Rezervaci jste odmítli, než jste ji potvrdili.",
  business_cancel: "Rezervaci jste zrušili poté, co jste ji potvrdili.",
  business_cannot_trade:
    "Rezervaci jsme zrušili, protože váš platební účet v tu chvíli nemohl přijmout platbu.",
  customer_storno: "Rezervaci zrušil zákazník.",
  checkout_abandoned: "Zákazník nedokončil platbu.",
};

function badgeFor(reservation: PartnerReservation, ending: CancellationEnding | null): StateBadge {
  if (ending !== null) return cancellationBadge[ending];
  return stateBadge[reservation.state] ?? { label: reservation.state, tone: "neutral" };
}

function cancellationOutcome(reservation: PartnerReservation): string | null {
  if (reservation.state !== "cancelled" || reservation.cancellationReason === null) return null;
  return outcomeByCancellationReason[reservation.cancellationReason] ?? null;
}

export function ReservationRow({
  reservation,
  isCancelling,
  onCancel,
  disclosure,
}: ReservationRowProps) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const isOnThisOffersOwnList = searchParams.get("offer") === String(reservation.offerId);
  const ending =
    reservation.state === "cancelled"
      ? cancellationEnding(reservation.cancellationReason)
      : null;
  const badge = badgeFor(reservation, ending);
  // Only this ending is about the deadline: on any other one `confirmDeadline`
  // is an instant that passed while the reservation was already gone, and the
  // payload carries no cancellation time to put in its place.
  const lapsedDeadline = ending === "expired" ? reservation.confirmDeadline : null;
  const termin = reservation.startTime ? formatDateTime(reservation.startTime) : "Termín neuveden";
  const customerName = reservation.customerName ?? "Zákazník";

  if (disclosure) {
    const outcome = cancellationOutcome(reservation);

    return (
      <li>
        <Collapsible open={disclosure.isExpanded} onOpenChange={disclosure.onExpandedChange}>
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&[data-state=open]>svg]:rotate-180">
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="truncate font-medium">{customerName}</span>
              <LGoBadge variant={badge.tone}>{badge.label}</LGoBadge>
            </span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform"
              aria-hidden="true"
            />
          </CollapsibleTrigger>

          <CollapsibleContent className="space-y-2 pb-4">
            <p className="text-sm text-muted-foreground">
              {reservation.activityName} · {termin} · {formatPrice(reservation.amountMinor)}
            </p>
            {reservation.code && (
              <p className="text-sm text-muted-foreground">
                Kód <span className="font-mono">{reservation.code}</span>
              </p>
            )}
            {outcome && <p className="text-sm">{outcome}</p>}
            {lapsedDeadline && (
              <LapsedConfirmationTimeline
                orderedAt={reservation.createdAt}
                deadline={lapsedDeadline}
              />
            )}
            {reservation.customAnswers.length > 0 && (
              <CustomFieldAnswers answers={reservation.customAnswers} />
            )}
          </CollapsibleContent>
        </Collapsible>
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-2 py-4 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium truncate">{customerName}</p>
          <LGoBadge variant={badge.tone}>{badge.label}</LGoBadge>
          {reservation.code && (
            <span className="text-sm font-mono text-muted-foreground">{reservation.code}</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {reservation.activityName} · {termin} · {formatPrice(reservation.amountMinor)}
        </p>
        {lapsedDeadline && (
          <LapsedConfirmationTimeline
            orderedAt={reservation.createdAt}
            deadline={lapsedDeadline}
          />
        )}
        {reservation.customAnswers.length > 0 && (
          <CustomFieldAnswers answers={reservation.customAnswers} />
        )}
      </div>

      {canBeCancelled(reservation) && (
        <div className="shrink-0">
          <LGoButton
            variant="ghost"
            iconLeft={<X />}
            onClick={() => setCancelOpen(true)}
            disabled={isCancelling}
          >
            Zrušit rezervaci
          </LGoButton>
        </div>
      )}

      <CancelReservationDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        customerName={customerName}
        wholeOfferId={isOnThisOffersOwnList ? null : reservation.offerId}
        isCancelling={isCancelling}
        onConfirm={() => onCancel(reservation.id, { onSettled: () => setCancelOpen(false) })}
      />
    </li>
  );
}
